import React, { useState } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { TrendingUp, TrendingDown, Minus, Search, MapPin, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useCountry } from '../contexts/CountryContext';
import logger from '../utils/logger';
import AIDisclaimer from './AIDisclaimer';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const trendIcons = {
  rising: <TrendingUp className="h-5 w-5 text-emerald-500" />,
  stable: <Minus className="h-5 w-5 text-amber-500" />,
  falling: <TrendingDown className="h-5 w-5 text-red-500" />,
};

const trendColors = {
  rising: 'text-emerald-600 bg-emerald-50 border-emerald-200',
  stable: 'text-amber-600 bg-amber-50 border-amber-200',
  falling: 'text-red-600 bg-red-50 border-red-200',
};

const demandColors = {
  high: 'bg-red-50 text-red-700 border-red-200',
  medium: 'bg-amber-50 text-amber-700 border-amber-200',
  low: 'bg-emerald-50 text-emerald-700 border-emerald-200',
};

const MarketTrends = () => {
  const { country } = useCountry();
  const [area, setArea] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchTrends = async () => {
    if (!area.trim()) { toast.error('Enter an area to search'); return; }
    setLoading(true);
    try {
      const res = await axios.post(`${API_URL}/api/market/trends`, { area: area.trim(), country });
      setData(res.data);
    } catch (err) {
      logger.error('Market trends error:', err);
      toast.error('Failed to load market data');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="market-trends-widget">
      {/* Search */}
      <div className="flex gap-2 mb-4">
        <div className="flex-1 relative">
          <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <input
            value={area}
            onChange={(e) => setArea(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && fetchTrends()}
            placeholder="Enter area, city or postcode..."
            className="w-full pl-9 pr-3 py-2.5 border border-slate-200 text-sm focus:border-[#E07A3A] focus:outline-none rounded-lg"
            data-testid="market-area-input"
          />
        </div>
        <Button
          onClick={fetchTrends}
          disabled={loading}
          className="bg-[#E07A3A] hover:bg-[#C4652E] text-white px-4 rounded-lg"
          data-testid="market-search-btn"
        >
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
        </Button>
      </div>

      {/* Results */}
      {data && (
        <div className="space-y-4 animate-in fade-in duration-300">
          {/* Price & Trend header */}
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-slate-400 uppercase tracking-wider">{data.area}, {data.country}</p>
              <p className="text-2xl font-['Playfair_Display'] text-[#1A1A1A] font-semibold">{data.average_price}</p>
            </div>
            <div className="text-right flex items-center gap-2">
              {trendIcons[data.price_trend] || trendIcons.stable}
              <div>
                <span className={`inline-block px-2 py-0.5 text-xs font-bold border rounded ${trendColors[data.price_trend] || trendColors.stable}`}>
                  {data.trend_percentage}
                </span>
                <p className="text-[10px] text-slate-400 mt-0.5">{data.price_trend}</p>
              </div>
            </div>
          </div>

          {/* Stats row */}
          <div className="grid grid-cols-2 gap-3">
            <div className="p-3 bg-slate-50 rounded-lg">
              <p className="text-[10px] text-slate-400 uppercase tracking-wider">Demand</p>
              <span className={`inline-block px-2 py-0.5 text-xs font-bold border rounded mt-1 ${demandColors[data.demand_level] || demandColors.medium}`}>
                {data.demand_level}
              </span>
            </div>
            <div className="p-3 bg-slate-50 rounded-lg">
              <p className="text-[10px] text-slate-400 uppercase tracking-wider">Time to Sell</p>
              <p className="text-sm font-semibold text-[#1A1A1A] mt-1">{data.time_to_sell}</p>
            </div>
          </div>

          {/* Summary */}
          <p className="text-sm text-slate-600 leading-relaxed">{data.summary}</p>

          {/* Tips */}
          {data.tips && data.tips.length > 0 && (
            <div className="bg-[#FEF3EC] p-3 rounded-lg">
              <p className="text-xs font-semibold text-[#E07A3A] mb-1.5 uppercase tracking-wider">Tips</p>
              {data.tips.map((tip, i) => (
                <p key={`tip-${tip.slice(0, 15)}`} className="text-xs text-slate-600 mb-1">• {tip}</p>
              ))}
            </div>
          )}

          {/* Comparable areas */}
          {data.comparable_areas && data.comparable_areas.length > 0 && (
            <div>
              <p className="text-[10px] text-slate-400 uppercase tracking-wider mb-1.5">Similar Areas</p>
              <div className="flex flex-wrap gap-1.5">
                {data.comparable_areas.map((a) => (
                  <button
                    key={a}
                    onClick={() => { setArea(a); }}
                    className="px-2.5 py-1 text-xs bg-white border border-slate-200 text-slate-600 hover:border-[#E07A3A] hover:text-[#E07A3A] transition-colors rounded-full"
                  >
                    {a}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {!data && !loading && (
        <div className="text-center py-8">
          <TrendingUp className="h-10 w-10 text-slate-200 mx-auto mb-2" />
          <p className="text-sm text-slate-400">Search any area to see pricing trends</p>
          <p className="text-xs text-slate-300">AI-powered analysis for UK, India & Canada</p>
          <AIDisclaimer compact />
        </div>
      )}

      {data && <AIDisclaimer compact />}
    </div>
  );
};

export default MarketTrends;
